// src/modules/customer/utils/exportCustomerList.js
//
// Müşteri listesi için EKSİKSİZ Excel export'u (salt-okunur, veri yazmaz).
// 3 sayfa üretir:
//   1. "Müşteriler"      -> her müşteri için tüm alanlar + finansal özet + cihaz sayısı
//   2. "Cihazlar"        -> kapsamdaki müşterilere ait tüm cihazlar
//   3. "Servis Geçmişi"  -> kapsamdaki müşterilere ait tüm servis kayıtları
//
// Kapsam (scope) çağıran tarafça belirlenir: "Görüntülenen" (aktif filtre) veya "Tümü".

import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

// Firestore Timestamp / Date / ISO-string / "yyyy-MM-dd" gibi farklı tarih
// formatlarını güvenli şekilde "dd.MM.yyyy" string'ine çevirir. Hatada boş döner.
const formatTarih = (value) => {
  if (!value) return '';
  try {
    if (value.toDate && typeof value.toDate === 'function') {
      return format(value.toDate(), 'dd.MM.yyyy', { locale: tr });
    }
    if (value instanceof Date) {
      return format(value, 'dd.MM.yyyy', { locale: tr });
    }
    if (typeof value === 'string') {
      const d = new Date(value);
      if (!isNaN(d.getTime())) return format(d, 'dd.MM.yyyy', { locale: tr });
      return value; // Tarihe çevrilemeyen string'i olduğu gibi koru
    }
  } catch (e) {
    // sessizce yut - export hiçbir zaman çökmemeli
  }
  return '';
};

// Para alanlarını 2 ondalığa yuvarlanmış SAYI olarak döndürür (Excel'de toplam/filtre yapılabilsin diye).
const toMoney = (value) => {
  const n = Number(value);
  if (!isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
};

const hizmetTuruText = (hizmetTuru) => {
  if (!hizmetTuru) return '';
  return Array.isArray(hizmetTuru) ? hizmetTuru.join(', ') : String(hizmetTuru);
};

/**
 * Müşteri listesini çok sayfalı bir Excel dosyası olarak indirir.
 *
 * @param {Object}   params
 * @param {Array}    params.customers       - Dışa aktarılacak müşteriler (financials ile zenginleştirilmiş: totalRevenue/totalPaid/balance).
 * @param {Array}    params.devices         - TÜM cihaz kayıtları (musteriId'ye göre filtrelenir).
 * @param {Array}    params.serviceRecords  - TÜM servis kayıtları (musteriId'ye göre filtrelenir).
 * @param {string}   [params.scopeLabel]    - Dosya adı eki, örn "Tumu" / "Goruntulenen".
 */
export const exportCustomerList = ({ customers = [], devices = [], serviceRecords = [], scopeLabel = 'Liste' }) => {
  const wb = XLSX.utils.book_new();

  const customerIds = new Set(customers.map((c) => c.id));
  const customerById = {};
  const deviceCountByCustomer = {};
  customers.forEach((c) => {
    customerById[c.id] = c;
    deviceCountByCustomer[c.id] = 0;
  });

  // Kapsamdaki cihaz/servis kayıtları
  const scopedDevices = devices.filter((d) => customerIds.has(d.musteriId));
  const scopedServices = serviceRecords.filter((s) => customerIds.has(s.musteriId));

  scopedDevices.forEach((d) => {
    if (deviceCountByCustomer[d.musteriId] !== undefined) {
      deviceCountByCustomer[d.musteriId] += 1;
    }
  });

  // 1) MÜŞTERİLER SAYFASI
  const customerRows = customers.map((c) => ({
    'Adı Soyadı': c.adSoyad || '',
    'TC Kimlik No': c.tcKimlikNo || '',
    'Telefon': c.telefon || '',
    'E-posta': c.email || '',
    'Adres': c.adres || '',
    'Hizmet Türü': hizmetTuruText(c.hizmetTuru),
    'SMS İzni': c.smsIzin === false ? 'Hayır' : 'Evet',
    'Cihaz Sayısı': deviceCountByCustomer[c.id] || 0,
    'Toplam Borç (₺)': toMoney(c.totalRevenue),
    'Toplam Ödenen (₺)': toMoney(c.totalPaid),
    'Bakiye (₺)': toMoney(c.balance),
    'Kayıt Tarihi': formatTarih(c.createdAt),
  }));

  const wsCustomers = XLSX.utils.json_to_sheet(
    customerRows.length > 0 ? customerRows : [{ Bilgi: 'Kayıt bulunamadı' }]
  );
  wsCustomers['!cols'] = [
    { wch: 24 }, // Adı Soyadı
    { wch: 14 }, // TC
    { wch: 15 }, // Telefon
    { wch: 24 }, // E-posta
    { wch: 32 }, // Adres
    { wch: 24 }, // Hizmet Türü
    { wch: 9 },  // SMS
    { wch: 12 }, // Cihaz Sayısı
    { wch: 16 }, // Toplam Borç
    { wch: 16 }, // Toplam Ödenen
    { wch: 14 }, // Bakiye
    { wch: 14 }, // Kayıt Tarihi
  ];
  XLSX.utils.book_append_sheet(wb, wsCustomers, 'Müşteriler');

  // 2) CİHAZLAR SAYFASI
  const deviceRows = scopedDevices.map((d) => {
    const owner = customerById[d.musteriId];
    return {
      'Müşteri': owner?.adSoyad || d.musteriAdSoyad || '',
      'Telefon': owner?.telefon || '',
      'Seri No': d.seriNumarasi || '',
      'Marka': d.marka || '',
      'Model': d.model || '',
      'Cihaz Sınıfı': d.cihazSinifi || '',
      'SGK': d.isSgk ? 'Evet' : 'Hayır',
      'Fatura No': d.faturaNo || '',
      'Veriliş Tarihi': formatTarih(d.verilisTarihi),
      'Zimmet Tarihi': formatTarih(d.zimmetTarihi),
      'Garanti Başlangıç': formatTarih(d.garantiBaslangic),
      'Garanti Bitiş': formatTarih(d.garantiBitis),
      'Periyodik Bakım': formatTarih(d.periyodikBakimTarihi),
      'Ekleme Tarihi': formatTarih(d.eklenmeTarihi || d.createdAt),
    };
  });

  const wsDevices = XLSX.utils.json_to_sheet(
    deviceRows.length > 0 ? deviceRows : [{ Bilgi: 'Kayıtlı cihaz yok' }]
  );
  wsDevices['!cols'] = [
    { wch: 24 }, { wch: 15 }, { wch: 18 }, { wch: 14 }, { wch: 16 },
    { wch: 18 }, { wch: 7 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
    { wch: 16 }, { wch: 14 }, { wch: 16 }, { wch: 14 },
  ];
  XLSX.utils.book_append_sheet(wb, wsDevices, 'Cihazlar');

  // 3) SERVİS GEÇMİŞİ SAYFASI
  const serviceRows = scopedServices.map((s) => {
    const owner = customerById[s.musteriId];
    const toplam = toMoney(s.toplamTutar);
    const odenen = toMoney(s.odenenTutar);
    return {
      'Müşteri': owner?.adSoyad || '',
      'Telefon': owner?.telefon || '',
      'Cihaz Seri No': s.cihazSeriNumarasi || '',
      'Durum': s.durum || '',
      'Tarih': formatTarih(s.olusturmaTarihi),
      'Toplam Tutar (₺)': toplam,
      'Ödenen (₺)': odenen,
      'Kalan (₺)': toMoney(toplam - odenen),
    };
  });

  const wsServices = XLSX.utils.json_to_sheet(
    serviceRows.length > 0 ? serviceRows : [{ Bilgi: 'Servis kaydı yok' }]
  );
  wsServices['!cols'] = [
    { wch: 24 }, { wch: 15 }, { wch: 18 }, { wch: 16 },
    { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 14 },
  ];
  XLSX.utils.book_append_sheet(wb, wsServices, 'Servis Geçmişi');

  // Dosyayı indir
  const tarihEki = format(new Date(), 'yyyy-MM-dd', { locale: tr });
  XLSX.writeFile(wb, `Musteri_Listesi_${scopeLabel}_${tarihEki}.xlsx`);
};

export default exportCustomerList;
